import React from 'react';
import { MessageSquare, Calculator, FileText } from 'lucide-react';

/**
 * SolutionsSection Component
 * 
 * Displays the three main solution cards (AI Chat, Tax Advisory, Contract Management)
 * with glassmorphic cards and icon gradients.
 * 
 * Exact styles from landing.css:
 * - Card padding: 2rem (32px)
 * - Border radius: 20px
 * - Background: rgba(255, 255, 255, 0.05) with backdrop blur 20px
 * - Card hover: translateY(-8px) with enhanced shadow
 * - Grid: repeat(auto-fit, minmax(280px, 1fr))
 */
const SolutionsSection = () => {
  const solutions = [
    {
      id: 1,
      title: 'AI Chat',
      description: 'Get instant answers to legal and financial questions with our AI-powered chatbot.',
      icon: MessageSquare,
      iconGradient: 'from-blue-500/20 to-purple-500/20',
    },
    {
      id: 2,
      title: 'Tax Advisory',
      description: 'Automate tax calculations, track deductions, and stay compliant with ease.',
      icon: Calculator,
      iconGradient: 'from-green-500/20 to-teal-500/20',
    },
    {
      id: 3,
      title: 'Contract Management',
      description: 'Upload, analyze, and manage legal contracts with AI-powered insights.',
      icon: FileText,
      iconGradient: 'from-orange-500/20 to-red-500/20',
    },
  ];

  return (
    <section id="solutions" className="section py-section-mobile md:py-section-tablet lg:py-section-desktop px-4 md:px-6 lg:px-8 max-w-[1200px] mx-auto relative z-10">
      <h2 className="section-title text-center text-mobile-section-title md:text-2xl lg:text-section-title font-medium mb-6 md:mb-10 lg:mb-12 text-text-primary leading-tight md:leading-normal">
        Our Solutions
      </h2>

      {/* Solutions Grid - MOBILE: Mobile-first spacing */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6 lg:gap-8">
        {solutions.map((solution) => {
          const IconComponent = solution.icon;
          return (
            <div
              key={solution.id}
              className="card p-4 md:p-6 lg:p-8 rounded-card glass-bg-card transition-all duration-300 shadow-card relative overflow-hidden group hover:-translate-y-2 hover:shadow-card-hover hover:border-white/20 active:scale-[0.99]"
            >
              {/* Card overlay gradient on hover */}
              <div className="absolute top-0 left-0 w-full h-full bg-card-overlay opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
              
              {/* Card radial overlay on hover */}
              <div className="absolute -top-1/2 -left-1/2 w-[200%] h-[200%] bg-card-radial-overlay opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

              {/* Icon with Gradient Background */}
              <div
                className={`w-16 h-16 rounded-full mb-4 flex items-center justify-center bg-gradient-to-br ${solution.iconGradient} backdrop-blur-sm border border-white/10 relative z-10 group-hover:scale-110 transition-transform duration-300`}
                style={{
                  filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))',
                  boxShadow: '0 4px 15px rgba(255, 255, 255, 0.1)',
                }}
              >
                <IconComponent 
                  className="w-8 h-8 text-white" 
                  strokeWidth={1.5}
                />
              </div>

              {/* Content */}
              <h3 className="text-lg md:text-xl lg:text-card-title mb-2 text-text-primary relative z-10">
                {solution.title}
              </h3>
              <p className="text-text-secondary text-sm md:text-base lg:text-card-text relative z-10">
                {solution.description}
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default SolutionsSection;

